import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TrialModal = ({ isOpen, onRequestClose, durationMs }) => {
    const timerRef = useRef(null);
    const [timeLeft, setTimeLeft] = useState('');
    const [progress, setProgress] = useState(100);

    useEffect(() => {
        if (!isOpen) return;
        // Changed default fallback from 60000 to 75000 (75 seconds)
        const totalTime = durationMs && Number.isFinite(durationMs) && durationMs > 0 ? Number(durationMs) : 75000;
        const startTime = Date.now();

        timerRef.current = setTimeout(() => { onRequestClose(); }, totalTime);

        const progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 100 - (elapsed / totalTime) * 100);
            setProgress(remaining);
        }, 100);

        const blockEscape = (e) => { if (e.key === 'Escape') e.preventDefault(); };
        document.addEventListener('keydown', blockEscape, true);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', blockEscape, true);
            document.body.style.overflow = '';
            if (timerRef.current) clearTimeout(timerRef.current);
            clearInterval(progressInterval);
        };
    }, [isOpen, onRequestClose, durationMs]);

    useEffect(() => {
        if (!isOpen) return;
        const endDate = new Date(2026, 1, 28, 23, 59, 59);
        const updateCountdown = () => {
            const now = new Date();
            const diffMs = endDate - now;
            if (diffMs <= 0) { setTimeLeft('DEACTIVATED'); return; }
            const seconds = Math.floor(diffMs / 1000);
            const days = Math.floor(seconds / 86400);
            const hours = Math.floor((seconds % 86400) / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            setTimeLeft(`${days}D : ${hours}H : ${minutes}M : ${secs}S`);
        };
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/95 backdrop-blur-md"
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{
                            scale: 1,
                            opacity: 1,
                            backgroundColor: ["#ffffff", "#fff5f5", "#ffffff"]
                        }}
                        transition={{
                            backgroundColor: { repeat: Infinity, duration: 1.2, ease: "easeInOut" },
                            scale: { type: "spring", stiffness: 300, damping: 25 }
                        }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white border-t-4 border-red-600 shadow-[0_0_60px_rgba(220,38,38,0.2)] max-w-lg w-full overflow-hidden"
                    >
                        {/* Status Header */}
                        <div className="bg-red-600 px-6 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                <span className="text-[10px] font-black tracking-widest text-white uppercase">
                                    Official Administrative Notice
                                </span>
                            </div>
                        </div>

                        {/* Body Section */}
                        <div className="p-10 text-left">
                            <h2 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">
                                Trial Subscription <span className="text-red-600">Expiring</span>
                            </h2>
                            <p className="text-sm text-slate-600 leading-relaxed mb-8">
                                Your Trial Subscription is scheduled for deactivation on <strong>February 28, 2026</strong>. To ensure uninterrupted service, please finalize your licensing agreement.
                            </p>

                            {/* The Timer Box */}
                            <div className="bg-slate-900 p-8 rounded-sm mb-8 relative overflow-hidden group">
                                <motion.div
                                    animate={{ opacity: [0.05, 0.15, 0.05] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                    className="absolute inset-0 bg-red-600"
                                />
                                <div className="relative z-10 text-center">
                                    <div className="text-[10px] text-slate-400 uppercase tracking-[0.3em] mb-3">Time Until Service Interruption</div>
                                    <div className="text-4xl font-mono text-white tracking-[0.15em] font-bold">
                                        {timeLeft}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <p className="text-[11px] text-slate-400 italic border-l-2 border-red-200 pl-4">
                                    Active sessions will self-terminate and administrative functions will be restricted following the conclusion of this period. Please contact your system provider to prevent downtime.
                                </p>
                            </div>
                        </div>

                        {/* Progress Bar (Timer) */}
                        <div className="h-2 w-full bg-slate-100">
                            <motion.div
                                className="h-full bg-red-600"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TrialModal;